import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useDispatch } from "react-redux";
import { addSite, getProject } from "../../store/slices/project";
import { toast } from 'react-toastify';
import * as XLSX from "xlsx";
import { Container, Spinner } from "react-bootstrap";


export default function AddSites() {
  const [project, setProject] = useState("");
  const dispatch = useDispatch();
  const [excelData, setExcelData] = useState([]);
  const [uploadingText, setUploadingText] = useState('');
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);


  const [projectError, setProjectError] = useState("");
  const [fileError, setFileError] = useState("");

  useEffect(() => {
    dispatch(getProject({}))
      .unwrap()
      .then((res) => {
        setProjects(res.data || []);
      })
      .catch(() => {
        setProjects([]);
      });
  }, [dispatch]);

  // Handle Excel upload
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setFileError("Please select a file.");
      return;
    }

    const reader = new FileReader();
    reader.readAsBinaryString(file);

    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      if (jsonData.length === 0) {
        setFileError("The file is empty or not formatted correctly.");
        return;
      }

      const formattedData = jsonData
        .slice(1) // remove header row
        .filter((row) =>
          row.some(
            (cell) =>
              cell !== undefined && cell !== null && String(cell).trim() !== ""
          )
        )
        .map((row) => ({
          plotNo: row[0]?.toString().trim() || "",
          facing: row[1]?.toString().trim() || "",
          dimension: row[2]?.toString().trim() || "",
          areaSqft: Number(row[3]) || 0,
          status: row[4]?.toString().trim() || "",
          plotPrice: Number(row[5]) || 0,
        }));

      if (formattedData.length === 0) {
        setFileError("The file does not contain valid site data.");
        setExcelData([]);
        return;
      }

      setFileError("");
      setExcelData(formattedData);
    };
  };

  // Upload data
  const handleSubmit = async () => {
    let hasError = false;

    if (!project) {
      setProjectError("Please select a project");
      hasError = true;
    } else setProjectError("");


    if (excelData.length === 0) {
      setFileError("Please upload a valid Excel file");
      hasError = true;
    } else setFileError("");

    if (hasError) return;

    try {
      setSubmitLoading(true);
      const payload = {
        project,
        sites: excelData,
      };
      await dispatch(addSite(payload)).unwrap();
      toast.success("Sites uploaded successfully!");
      navigate(-1);
    } catch (error) {
      toast.error(error || "Failed to upload data. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  //  Generate downloadable Excel template with dropdowns (no sample rows)
  const handleDownloadFormat = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("BulkSiteTemplate");

    // Headers
    const headers = [
      "Plot No",
      "Facing",
      "Dimension",
      "Area Sqft",
      "Status",
      "Plot Price",
    ];
    worksheet.addRow(headers);

    const statusOptions = [
      "Open", "Booked", "Reserved", "Sold"
    ];
    const facingOptions = [
      "EAST",
      "WEST",
      "NORTH",
      "SOUTH",
      "NORTH_EAST",
      "NORTH_WEST",
      "SOUTH_EAST",
      "SOUTH_WEST",
    ];


    // Add 50 empty rows for users to fill
    for (let i = 0; i < 50; i++) {
      worksheet.addRow([]);
    }

    // Apply dropdowns to Type (col 4), Status (col 5), Source (col 6)
    for (let row = 2; row <= 51; row++) {
      // Facing dropdown (Column B)
      worksheet.getCell(`B${row}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`"${facingOptions.join(",")}"`],
        showErrorMessage: true,
        errorTitle: "Invalid Input",
        error: "Please select a valid Facing",
      };

      // Status dropdown (Column E)
      worksheet.getCell(`E${row}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`"${statusOptions.join(",")}"`],
        showErrorMessage: true,
        errorTitle: "Invalid Input",
        error: "Please select a valid Status",
      };
    }

    // Adjust column widths
    worksheet.columns = [
      { width: 20 },
      { width: 15 },
      { width: 25 },
      { width: 15 },
      { width: 18 },
      { width: 18 },
      { width: 15 },
      { width: 12 },
      { width: 15 },
      { width: 15 },
    ];

    // Style header
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).alignment = { horizontal: "center" };

    // Download
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Site_Upload_Template.xlsx");
  };

  return (
    <>
      <div className="breadcrumb-bar sticky-top mx-1">
        <div className="container-fluid">
          <div className="d-flex flex-row align-items-center justify-content-between gap-2 py-2">
            <div className="breadcrumb-left d-flex align-items-center flex-wrap">
              <Link to="/admin/home" className="breadcrumb-link active">
                Home
              </Link>
              <span className="breadcrumb-separator mx-2">
                <i className="bi bi-chevron-right"></i>
              </span>
              <Link onClick={() => navigate(-1)} className="breadcrumb-link active">
                Sites
              </Link>
              <span className="breadcrumb-separator mx-2">
                <i className="bi bi-chevron-right"></i>
              </span>

              <span className="breadcrumb-link inactive">
                Add Sites
              </span>
            </div>

          </div>
        </div>
      </div>

      <div className="ud-page-wrap">
        <Container fluid>
          <div className="ud-header">
            <h2 className="ud-main-title">Add Site</h2>

          </div>
          <div className="global-card">
            <div className="row">
              <div className="col-lg-9 ">

                <div className="d-flex justify-content-left align-items-center gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleDownloadFormat}
                  >
                    Download Format
                  </button>
                </div>
                <p className="text-muted medium text-start">
                  <strong>Note:</strong> Please upload site data in the same
                  format as provided in the template and don't modify the header
                  names.
                </p>


                <div className="row mb-4 align-items-center">


                  <div className="col-lg-4">
                    <label className="fw-semibold">Project </label>
                    <select
                      className={`form-select mt-2 ${projectError ? "is-invalid" : ""
                        }`}
                      value={project}
                      onChange={(e) => {
                        setProject(e.target.value);
                        setProjectError("");
                      }}
                    >
                      <option value="">Select</option>
                      {projects.map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.projectName}
                        </option>
                      ))}
                    </select>
                    {projectError && (
                      <div className="invalid-feedback">{projectError}</div>
                    )}
                  </div>

                  <div className="col-lg-4">
                    <label className="fw-semibold">File: </label>
                    <input
                      type="file"
                      className={`form-control mt-2 ${fileError ? "is-invalid" : ""
                        }`}
                      onChange={handleFileChange}
                    />
                    {fileError && (
                      <div className="invalid-feedback">{fileError}</div>
                    )}
                  </div>
                </div>

                <div className="row mb-4 justify-content-center align-items-center">
                  <div className="col-md-4 d-flex gap-2">
                    <button
                      type="button"
                      className="global-cancel-btn"
                      onClick={() => navigate(-1)}
                      disabled={submitLoading}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="global-submit-btn"
                      onClick={handleSubmit}
                      disabled={submitLoading}
                    >
                      {submitLoading ? (
                        <span className="d-flex align-items-center gap-2">
                          <Spinner animation="border" size="sm" />
                          {uploadingText || 'Saving…'}
                        </span>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </Container>
      </div>
    </>
  );
};
